import React, { useEffect } from 'react'
import {
  formatEventTimeRange,
  formatModalDate,
  getEventsForDate,
} from './dateUtils'
import { AvailabilityCalendarEvent, AvailabilityCalendarLabels } from './types'
import {
  DayEventsClose,
  DayEventsCount,
  DayEventsDate,
  DayEventsDayName,
  DayEventsDialog,
  DayEventsHeader,
  DayEventsItem,
  DayEventsItemColor,
  DayEventsItemDetails,
  DayEventsItemInfo,
  DayEventsItemTitle,
  DayEventsList,
  DayEventsOverlay,
} from './style'

interface DayEventsModalProps {
  date: Date | null
  events: AvailabilityCalendarEvent[]
  labels: AvailabilityCalendarLabels
  onClose: () => void
}

export const DayEventsModal = ({
  date,
  events,
  labels,
  onClose,
}: DayEventsModalProps): JSX.Element | null => {
  const dayEvents = date != null ? getEventsForDate(events, date) : []
  const modalDate = date != null ? formatModalDate(date) : null

  useEffect(() => {
    if (date == null) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [date, onClose])

  if (date == null || modalDate == null) return null

  return (
    <DayEventsOverlay onClick={onClose} role="presentation">
      <DayEventsDialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="day-events-title"
        onClick={event => event.stopPropagation()}
      >
        <DayEventsHeader>
          <DayEventsDate>
            <DayEventsDayName id="day-events-title">
              {modalDate.dayName}
            </DayEventsDayName>
            <div>{modalDate.fullDate}</div>
          </DayEventsDate>
          <DayEventsClose type="button" aria-label={labels.close} onClick={onClose}>
            ×
          </DayEventsClose>
        </DayEventsHeader>

        <DayEventsCount>
          {labels.reservationsCount(dayEvents.length)}
        </DayEventsCount>

        <DayEventsList>
          {dayEvents.map(event => {
            const timeRange = formatEventTimeRange(event)
            return (
              <DayEventsItem key={event.id}>
                <DayEventsItemColor $color={event.color} />
                <DayEventsItemInfo>
                  <DayEventsItemTitle>{timeRange}</DayEventsItemTitle>
                  <DayEventsItemDetails>
                    {event.title} · {event.roomName}
                  </DayEventsItemDetails>
                </DayEventsItemInfo>
              </DayEventsItem>
            )
          })}
        </DayEventsList>
      </DayEventsDialog>
    </DayEventsOverlay>
  )
}
